import { setLoginContext } from 'app/utils/auth';
import { consumeReturnLocation } from 'app/utils/sessionReturnLocation';
import { logger } from 'shared/infrastructure/logger';

/**
 * Determines the redirect path when the user logs in with a token.
 *
 * Handles three scenarios:
 * 1. Deep link paths (path segments > 4) - extracts and returns the deep link path
 * 2. Valid redirect_uri query parameter - redirects via window.location.href
 * 3. No redirect_uri / fallback - returns '/home'
 *
 * Also validates redirect_uri to prevent open redirect vulnerabilities.
 * Invalid redirect_uri values are logged to Sentry and redirect to '/not-found'.
 *
 * @param {object} options
 * @param {Function} options.dispatch - Redux dispatch function
 * @param {object} options.coreEntities - Core entities with auth actions
 * @returns {string} The path to redirect to
 */
export function redirectFromLoginWithToken({ dispatch, coreEntities }) {
  const path = window.location.pathname;
  const queryParams = window.location.search;
  const urlParams = new URLSearchParams(queryParams);
  const redirectUri = urlParams.get('redirect_uri');

  if (path.startsWith('/saml/callback/')) {
    setLoginContext(queryParams, dispatch);
  }

  if (path.startsWith('/auth/login/') || path.includes('/saml/callback/')) {
    const splitted = path.split('/');
    if (splitted.length > 4) {
      const newPath = '/' + splitted[splitted.length - 1].replaceAll('&', '/');

      dispatch(coreEntities.auth.actions.requestUser());

      return queryParams ? `${newPath}${queryParams}` : newPath;
    }
  }

  if (redirectUri) {
    try {
      const parsedUrl = new URL(redirectUri, window.location.origin);

      // Same-origin: use React Router (client-side navigation, no page reload)
      // This avoids a race condition where window.location.href reload
      // can happen before redux-persist writes the auth state to localStorage
      if (parsedUrl.origin === window.location.origin) {
        return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
      }

      // Cross-origin: full page navigation required
      window.location.href = redirectUri;
      return '/home';
    } catch {
      logger.warn('Invalid redirect_uri in login', {
        tags: { page: 'login_redirect' },
        extra: { redirectUri, currentPath: path },
      });
      return '/not-found';
    }
  }

  // No redirect_uri: honor a one-shot return location saved before a session
  // timeout re-login (see SessionTimeout). `consumeReturnLocation` re-validates
  // the stored path as safe/same-origin, so it inherits the same open-redirect
  // protection. Falls back to '/home' for normal logins.
  return consumeReturnLocation() ?? '/home';
}
